import type { Coding } from '@medplum/fhirtypes';

import { slugify } from '@eluve/plan-shapes';

export const MEASURE_CODE_SYSTEM = 'https://eluve.com/CodeSystem/measure';

export type MeasureKind = 'quantity' | 'string';

export type MeasureDef = {
  key: string;
  label: string;
  system: string;
  code: string;
  display: string;
  unit?: string;
  kind: MeasureKind;
};

const DEFAULT_MEASURES: MeasureDef[] = [
  {
    key: 'nprs',
    label: 'Pain severity (NPRS 0-10)',
    system: 'http://loinc.org',
    code: '72514-3',
    display: 'Pain severity - 0-10 verbal numeric rating [Reported]',
    unit: '{score}',
    kind: 'quantity',
  },
  {
    key: 'oswestry',
    label: 'Oswestry Disability Index (%)',
    system: 'http://loinc.org',
    code: '97770-0',
    display: 'Oswestry Disability Index',
    unit: '%',
    kind: 'quantity',
  },
  {
    key: 'shoulder-internal-rotation',
    label: 'Shoulder internal rotation (degrees)',
    system: MEASURE_CODE_SYSTEM,
    code: 'shoulder-internal-rotation',
    display: 'Shoulder internal rotation',
    unit: 'deg',
    kind: 'quantity',
  },
  {
    key: 'shoulder-abduction-arom',
    label: 'Shoulder abduction (AROM)',
    system: MEASURE_CODE_SYSTEM,
    code: 'shoulder-abduction-arom',
    display: 'Shoulder abduction active range of motion',
    unit: 'deg',
    kind: 'quantity',
  },
  {
    key: 'lumbar-flexion-fingertip-floor',
    label: 'Lumbar forward-flexion / toe-touch (fingertip-to-floor)',
    system: MEASURE_CODE_SYSTEM,
    code: 'lumbar-flexion-fingertip-floor',
    display: 'Lumbar flexion fingertip-to-floor distance',
    unit: 'cm',
    kind: 'quantity',
  },
  {
    key: 'hba1c',
    label: 'Hemoglobin A1c (%)',
    system: 'http://loinc.org',
    code: '4548-4',
    display: 'Hemoglobin A1c/Hemoglobin.total in Blood',
    unit: '%',
    kind: 'quantity',
  },
  {
    key: 'monthly-headache-days',
    label: 'Monthly headache days',
    system: MEASURE_CODE_SYSTEM,
    code: 'monthly-headache-days',
    display: 'Headache days per month',
    unit: '{days}/mo',
    kind: 'quantity',
  },
  {
    key: 'cervical-flexion-arom',
    label: 'Cervical flexion (AROM)',
    system: MEASURE_CODE_SYSTEM,
    code: 'cervical-flexion-arom',
    display: 'Cervical flexion active range of motion',
    unit: 'deg',
    kind: 'quantity',
  },
  {
    key: 'cervical-rotation-arom-right',
    label: 'Cervical rotation R (AROM)',
    system: MEASURE_CODE_SYSTEM,
    code: 'cervical-rotation-arom-right',
    display: 'Cervical rotation active range of motion, right',
    unit: 'deg',
    kind: 'quantity',
  },
  {
    key: 'cervical-rotation-arom-left',
    label: 'Cervical rotation L (AROM)',
    system: MEASURE_CODE_SYSTEM,
    code: 'cervical-rotation-arom-left',
    display: 'Cervical rotation active range of motion, left',
    unit: 'deg',
    kind: 'quantity',
  },
  {
    key: 'ccft-endurance',
    label: 'Craniocervical flexion endurance (CCFT)',
    system: MEASURE_CODE_SYSTEM,
    code: 'ccft-endurance',
    display: 'Craniocervical flexion test endurance hold',
    unit: 's',
    kind: 'quantity',
  },
];

export const DEFAULT_MEASURE_CATALOG: readonly MeasureDef[] = Object.freeze(
  DEFAULT_MEASURES.map((measure) => Object.freeze(measure)),
);

/** @deprecated Resolve a tenant catalog at the app boundary in production. */
export const MEASURES = DEFAULT_MEASURE_CATALOG;

export const measureToken = (m: MeasureDef): string => `${m.system}|${m.code}`;

export const matchCatalogMeasure = (
  measures: readonly MeasureDef[],
  coding: Pick<Coding, 'system' | 'code'> | undefined,
): MeasureDef | undefined => {
  if (!coding?.system || !coding.code) return undefined;
  return measures.find(
    (m) => m.system === coding.system && m.code === coding.code,
  );
};

const LOCAL_MEASURE_SYSTEMS = new Set([
  MEASURE_CODE_SYSTEM,
  // The pre-catalog local system some historical observations were coded under.
  'https://eluve.com/measure',
]);

/**
 * The one measure-identity resolver: exact system|code first, then a
 * migration-era coalescer for legacy LOCAL code drift (e.g.
 * `shoulder-abduction` / `active-shoulder-abduction-range-of-motion` →
 * `shoulder-abduction-arom`). Foreign systems (LOINC, …) never fuzzy-match.
 * Used by the view model, longitudinal series, goal-target joins, and recap
 * tracking so a legacy observation always lands on its canonical row.
 */
export const resolveCatalogMeasure = (
  measures: readonly MeasureDef[],
  coding: Pick<Coding, 'system' | 'code'> | undefined,
  text: string | undefined,
): MeasureDef | undefined => {
  const exact = matchCatalogMeasure(measures, coding);
  if (exact) return exact;

  if (coding?.system && !LOCAL_MEASURE_SYSTEMS.has(coding.system)) {
    return undefined;
  }

  const candidateKeys = (source: string | undefined): string[] => {
    const words = slugify(source ?? '')
      .split('-')
      .filter(Boolean);
    const activeRange = words.includes('active') || words.includes('arom');
    const semanticWords = words.filter(
      (word) =>
        ![
          'active',
          'arom',
          'left',
          'right',
          'bilateral',
          'range',
          'of',
          'motion',
        ].includes(word),
    );
    if (semanticWords.length === 0) return [];
    const base = semanticWords.join('-');
    // A bare legacy code ("shoulder-abduction") predates the -arom suffix, so
    // the suffixed key is always a candidate.
    return activeRange ? [`${base}-arom`] : [base, `${base}-arom`];
  };
  for (const key of [...candidateKeys(coding?.code), ...candidateKeys(text)]) {
    const entry = measures.find((measure) => measure.key === key);
    if (entry) return entry;
  }
  return undefined;
};

export const formatCatalog = (measures: readonly MeasureDef[]): string => {
  if (measures.length === 0) return '(no measure catalog provided)';
  return measures
    .map(
      (e) =>
        `- ${e.label} → ${e.system}|${e.code}${e.display ? ` (${e.display})` : ''}` +
        ` [${e.kind}${e.unit ? `, unit ${e.unit}` : ''}]`,
    )
    .join('\n');
};
